import React, { useRef, useEffect, useState, useCallback } from 'react'
import { ManageProfiles } from '@pages/ManageProfiles/ManageProfiles'
import { Logout } from '@components/Settings/Logout/Logout'
import { Legal } from '@components/Settings/Legal/Legal'
import { useDispatch } from 'react-redux'
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import { SideMenu } from '@components/SideMenu/SideMenu'
import { DynamicPage } from '@pages/DynamicPage/DynamicPage'
import { Search } from '@pages/Search/Search'
import { Player } from '@components/Player/Player'
import { convertHexToRgb, openMenuOnBackKey, fallbackTheme, getActiveProfile } from '@utils/helpers'
import ProtectedAuthRoutes from '@components/ProtectedRoutes/ProtectedAuthRoutes'
import ProtectedHomeRoutes from '@components/ProtectedRoutes/ProtectedHomeRoutes'
import MyList from '@pages/MyList/MyList'
import { useTypedSelector } from './store'
import Category from '@pages/Category/Category'
import Skeleton from '@pages/Skeleton/Skeleton'
import InitialScreen from '@components/InitialScreen/InitialScreen'
import { loginRoutes, mainRoutes, profileRoutes, routes, settingsRoutes } from '@constants/routes.const'
import Register from '@pages/Register/Register'
import { Login } from '@pages/Login/Login'
import { ForgotPassword } from '@pages/Login/ForgotPassword/ForgotPassword'
import { PinAndPair } from '@pages/Login/PinAndPair/PinAndPair'
import { AccountError } from '@pages/Login/AccountError/AccountError'
import AppLanguage from '@pages/ManageProfiles/EditProfile/AppLanguage/AppLanguage'
import { getLanguage } from '@utils/vocab'
import { initializeSpatialNavigation, scrollNavigate, scrollScreen } from '@utils/scroll.utils'
import '@splidejs/splide/dist/css/splide.min.css'
import PageNotFound from '@components/PageNotFound/PageNotFound'
import ErrorCodeModal from '@components/ErrorCodeModal/ErrorCodeModal'
import useNetwork from '@hooks/useNetwork'
import { setError } from '@actions/errorCodeActions'
import { setPageNotFound } from '@actions/pageNotFoundActions'
import { ErrorCodes, ErrorActions, Notification, LocalStorageItem } from './constants'
import APP_CONFIG from './app_config.json'
import { VelvetSDK } from 'sdk'
import ProfileName from '@pages/ManageProfiles/EditProfile/ProfileName/ProfileName'
import { ThemeType } from 'sdk/services/theme/types/theme'
import { ConfigurationType } from 'sdk/services/config/types/config'
import { fetchCountry } from '@actions/userCountryActions'
import { Settings } from '@pages/Settings/Settings'
import { EditProfile } from '@pages/ManageProfiles/EditProfile/EditProfile'
import { CreateNewProfile } from '@pages/ManageProfiles/CreateNewProfile/CreateNewProfile'
import { DeleteProfile } from '@pages/ManageProfiles/EditProfile/DeleteProfile/DeleteProfile'
import { PasswordConfirmation } from '@pages/ManageProfiles/EditProfile/PasswordConfirmation/PasswordConfirmation'
import { ContentClassification } from '@pages/ManageProfiles/EditProfile/ContentClassification/ContentClassification'
import { PinSetup } from '@pages/ManageProfiles/EditProfile/PinSetup/PinSetup'
import { useUserProfiles } from '@hooks/useUserProfiles'
import { ChooseIcon } from '@pages/ManageProfiles/EditProfile/ChooseIcon/ChooseIcon'

function App() {
  const location = useLocation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [menu, setMenu] = useState(false)
  const showSkeleton = useTypedSelector((state) => state.skeleton.show)
  const showErrorModal = useTypedSelector((state) => state.errorCode.showErrorModal)
  const errorCode = useTypedSelector((state) => state.errorCode)
  const isFetchingUserCountry = useTypedSelector((state) => state.userCountry.loading)
  const showMenu = (status: boolean) => setMenu(status)
  const userObject = JSON.parse(localStorage.getItem(LocalStorageItem.USER_DATA) as string)
  const [toFetchVocab, setToFetchVocab] = useState(true)
  const keyPressed = useRef(false)
  const keyPressedTimeout = useRef(0)
  const showPageNotFound = useTypedSelector((state) => state.pageNotFound.show)
  const isOnline = useNetwork()
  const [config, setConfig] = useState<ConfigurationType | null>(null)
  const fetchVocabCondition = toFetchVocab && config
  const [isVocabFetching, setIsVocabFetching] = useState(true)
  const initialRoute = useTypedSelector((state) => state.menu.initialRoute)
  const [isUserManagementConfigured, setIsUserManagementConfigured] = useState(false)
  const activeProfile = getActiveProfile()

  const configService = useCallback(async () => {
    if (process.env.REACT_APP_BASE_URL) {
      await VelvetSDK.Configuration.service.fetchConfiguration(process.env.REACT_APP_BASE_URL)
    }
  }, [])

  const fetchUserCountry = useCallback(async () => {
    await dispatch(fetchCountry())
  }, [dispatch])

  const configureUserManagement = useCallback(async () => {
    await VelvetSDK.Authentication.service.configure()
    setIsUserManagementConfigured(true)
  }, [])

  const handleMessage = useCallback((event: MessageEvent<{ type: string }>) => {
    if (event.data.type === Notification.LOGOUT_USER) {
      VelvetSDK.Authentication.service.logout()
    }
  }, [])

  useUserProfiles({ isUserManagementConfigured })

  useEffect(() => {
    window.addEventListener('message', handleMessage)
    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, [handleMessage])

  useEffect(() => {
    if (config) {
      configureUserManagement()
    }
  }, [config, configureUserManagement])

  const fetchConfig = useCallback(async () => {
    await configService()

    if (VelvetSDK.Configuration.configuration?.error) {
      const error = VelvetSDK.Configuration.configuration.error

      const errorUpdate = {
        showErrorModal: true,
        errorCode: ErrorCodes.FETCH_CONFIG,
        HTTPcode: error.status,
        fetchAction: ErrorActions.CONFIG,
      }

      dispatch(setError(errorUpdate))
      dispatch(setConfig(null))
    } else {
      setConfig(VelvetSDK.Configuration.configuration)

      if (errorCode.fetchAction === ErrorActions.CONFIG) {
        dispatch(setError({ errorCode: '', showErrorModal: false }))
      }
    }
  }, [configService, dispatch, errorCode.fetchAction])

  const fetchVocabulary = useCallback(
    async (language?: string | null) => {
      await VelvetSDK.Vocabulary.service.fetchVocabulary(language)

      if (VelvetSDK.Vocabulary.vocabulary.error) {
        const error = VelvetSDK.Vocabulary.vocabulary.error
        const errorUpdate = {
          showErrorModal: true,
          errorCode: ErrorCodes.FETCH_VOCABULARY,
          HTTPcode: error.status,
          fetchAction: ErrorActions.VOCABULARY,
        }

        dispatch(setError(errorUpdate))
      } else {
        errorCode.fetchAction === ErrorActions.VOCABULARY &&
          dispatch(setError({ errorCode: '', showErrorModal: false }))
      }

      setIsVocabFetching(VelvetSDK.Vocabulary.isVocabFetching)
    },
    [dispatch, errorCode.fetchAction],
  )

  const getVocabWithUsersLanguage = useCallback(async () => {
    if (fetchVocabCondition) {
      const language = await getLanguage()
      // If user is not logged in, always fetch the English vocab.
      // If user is logged in, fetch the preferred language
      fetchVocabulary(language)
      setToFetchVocab(false)
    }
  }, [fetchVocabCondition, fetchVocabulary])

  useEffect(() => {
    VelvetSDK.configure(APP_CONFIG)
  }, [])

  useEffect(() => {
    dispatch(setError({ showErrorModal: isOnline ? false : true }))
  }, [dispatch, isOnline, navigate])

  useEffect(() => {
    initializeSpatialNavigation()
  }, [])

  useEffect(() => {
    fetchConfig()
    // do not update the dependency array, it re-fetches
    // the config when the error state is updated.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (config?.apiEndpointBaseUrl ) {
      fetchUserCountry();
    }
  }, [config?.apiEndpointBaseUrl, fetchUserCountry])
  useEffect(() => {
    config && getVocabWithUsersLanguage()
  }, [config, getVocabWithUsersLanguage])

  useEffect(() => {
    document.addEventListener('sn:focused', scrollScreen)

    return () => document.removeEventListener('sn:focused', scrollScreen)
  }, [])

  useEffect(() => {
    document.addEventListener('wheel', (e) => scrollNavigate(e, keyPressed, keyPressedTimeout), { passive: false })

    return () => {
      document.removeEventListener('wheel', (e) => scrollNavigate(e, keyPressed, keyPressedTimeout))
    }
  }, [])

  useEffect(() => {
    if (config) {
      const theme = VelvetSDK.Theme.service.getTheme(config.theme)

      for (let color in theme) {
        const colorValue = theme[color as keyof ThemeType]

        if (typeof colorValue !== 'string') {
          if (colorValue?.type === 'gradient') {
            const gradient = `linear-gradient(${colorValue.from}, ${colorValue.to})`
            document.documentElement.style.setProperty(`--${color}`, gradient)
            colorValue.to && document.documentElement.style.setProperty(`--bgGradientTo`, colorValue.to)
          }
        } else {
          document.documentElement.style.setProperty(`--${color}`, colorValue)
          document.documentElement.style.setProperty(`--${color}-rgb`, convertHexToRgb(colorValue))
        }
      }
    } else {
      fallbackTheme()
    }
  }, [config])

  useEffect(() => {
    if (
      !location.pathname.startsWith(routes.MOVIES + '/') &&
      !location.pathname.startsWith(routes.SERIES + '/') &&
      !showSkeleton
    ) {
      document.addEventListener('keydown', openMenuOnBackKey)
      return () => document.removeEventListener('keydown', openMenuOnBackKey)
    }
  }, [location.pathname, showSkeleton])

  useEffect(() => {
    const staticPaths = [routes.SEARCH, routes.MY_LIST, routes.SETTINGS, routes.MANAGE_PROFILES]

    staticPaths.includes(location.pathname) && dispatch(setPageNotFound(false))
  }, [dispatch, location.pathname])

  useEffect(() => {
    const historyEntry = {
      path: location.pathname,
    }

    // Retrieve the navigation history from sessionStorage
    let storedHistory = JSON.parse(sessionStorage.getItem('navigationHistory') || '[]')
    const isHomePage =
      (initialRoute && location.pathname.includes(initialRoute)) || location.pathname.includes(mainRoutes.HOME)

    // Check if path contains allowed routes (home, movie, serie)
    const isAllowedRoute =
      location.pathname.includes(mainRoutes.HOME) ||
      location.pathname.includes(mainRoutes.MOVIE) ||
      location.pathname.includes(mainRoutes.SERIES)

    // Clear history on home pages
    if (isHomePage) {
      storedHistory = [historyEntry]
    } else {
      const lastEntry = storedHistory[storedHistory.length - 1]

      // Only add to history if:
      // 1. It's an allowed route (home/movie/serie)
      // 2. Not a duplicate of last entry
      if (
        isAllowedRoute &&
        (!lastEntry || lastEntry.path !== location.pathname) &&
        !location.pathname.includes(routes.PLAYER)
      ) {
        storedHistory.push(historyEntry)
      }
    }

    // Save the updated history to sessionStorage
    sessionStorage.setItem('navigationHistory', JSON.stringify(storedHistory))
  }, [initialRoute, location.pathname])

  return (
    <div className="App">
      {userObject && isOnline && !showErrorModal && isUserManagementConfigured && activeProfile && !isFetchingUserCountry && (
        <div className="app-side-menu">
          <SideMenu showMenu={menu} />
        </div>
      )}
      <div className="app-content">
        <div className="background"></div>
        {showSkeleton && location.pathname !== '/login' && <Skeleton />}
        {showErrorModal && !showPageNotFound && (
          <ErrorCodeModal showMenu={showMenu} isOffline={isOnline ? false : true} />
        )}
        {showPageNotFound && !showErrorModal && <PageNotFound showMenu={showMenu} />}
        {!isVocabFetching && isOnline && !showErrorModal && (
          <Routes>
            <Route element={<ProtectedAuthRoutes />}>
              <Route path={routes.INITIAL_PAGE} element={<InitialScreen showMenu={showMenu} />} />
              <Route path={routes.LOGIN}>
                <Route index element={<Login showMenu={showMenu} />} />
                <Route path={loginRoutes.FORGOT_PASSWORD} element={<ForgotPassword showMenu={showMenu} />} />
                <Route path={loginRoutes.PIN_AND_PAIR} element={<PinAndPair showMenu={showMenu} />} />
                <Route path={loginRoutes.ACCOUNT_ERROR} element={<AccountError showMenu={showMenu} />} />
              </Route>
              <Route path={routes.REGISTER} element={<Register showMenu={showMenu} />} />
            </Route>
            <Route element={<ProtectedHomeRoutes />}>
              <Route path={routes.SEARCH} element={<Search showMenu={showMenu} />} />
              <Route path={routes.MY_LIST} element={<MyList showMenu={showMenu} />} />
              <Route path={`${routes.CATEGORY}/:chosenCategory`} element={<Category showMenu={showMenu} />} />
              <Route path={`${routes.PLAYER}/:slug`} element={<Player showMenu={showMenu} />} />

              <Route path={routes.SETTINGS}>
                <Route index element={<Settings showMenu={showMenu} />} />
                <Route path={settingsRoutes.LEGAL} element={<Legal showMenu={showMenu} />} />
                <Route path={settingsRoutes.LOGOUT} element={<Logout showMenu={showMenu} />} />
              </Route>

              <Route path={routes.MANAGE_PROFILES}>
                <Route index element={<ManageProfiles showMenu={showMenu} />} />
                <Route path={`${profileRoutes.EDIT_PROFILE}/:profileId`}>
                  <Route index element={<EditProfile showMenu={showMenu} />} />
                  <Route path={profileRoutes.EDIT_LANG} element={<AppLanguage showMenu={showMenu} />} />
                  <Route path={profileRoutes.EDIT_NAME} element={<ProfileName showMenu={showMenu} />} />
                  <Route path={profileRoutes.DELETE_PROFILE} element={<DeleteProfile showMenu={showMenu} />} />
                  <Route path={profileRoutes.PIN_SETUP} element={<PinSetup showMenu={showMenu} />} />
                  <Route path={profileRoutes.CHOOSE_ICON} element={<ChooseIcon showMenu={showMenu} />} />
                  <Route
                    path={profileRoutes.PASSWORD_CONFIRMATION}
                    element={<PasswordConfirmation showMenu={showMenu} />}
                  />
                  <Route
                    path={profileRoutes.CONTENT_CLASSIFICATION}
                    element={<ContentClassification showMenu={showMenu} />}
                  />
                </Route>
                <Route path={profileRoutes.CREATE_NEW_PROFILE} element={<CreateNewProfile showMenu={showMenu} />} />
              </Route>

              <Route path="/*" element={<DynamicPage showMenu={showMenu} />} />
            </Route>
          </Routes>
        )}
      </div>
    </div>
  )
}

export default App
